import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { injectIntl, FormattedMessage } from '../../util/reactIntl';
import { parse, stringify } from '../../util/urlHelpers';

import DateInput from '../../components/FieldDateInput/DateInput';
import ToggleSwitch from '../../components/ToggleSwitch/ToggleSwitch';
import { InlineTextButton, Input, Select } from '../../components';
import AutocompleteSelect from '../../components/AutocompleteSelect/AutocompleteSelect';

import css from './InboxPage.css';

const COUNTRY_PL = 'pl';

export const isCountry = (countryCode) => process.env.REACT_APP_COUNTRY_ID === countryCode;
export const isPoland = () => isCountry(COUNTRY_PL);

const SELECT_OPTIONS = [
  { value: "all", label: "TransactionFilter.allBookings", sales: true },
  { value: "pending-payment", label: "TransactionFilter.pendingPayment", sales: false },
  { value: "payment-expired", label: "TransactionFilter.paymentExpired", sales: false },
  { value: "accepted", label: "TransactionFilter.accepted", sales: true },
  { value: "picked-up", label: "TransactionFilter.pickedUp", sales: true },
  { value: "delivered", label: "TransactionFilter.delivered", sales: true },
  { value: "cancelled", label: "TransactionFilter.cancelled", sales: true },
];

const INTERNAL_USE_OPTIONS = [
  { value: "all", label: "Wszystkie rezerwacje" },
  { value: 'internal', label: "Tylko wewnętrzne" },
  { value: 'external', label: "Bez wewnętrznych" },
];

const STATUSES_FOR_DATE_FILTER = ["accepted", "picked-up", "delivered"];

const TransactionsFilter = ({
  providerId,
  currentUserProviders,
  isSales,
  pagination,
  intl
}) => {
  const history = useHistory();
  const location = useLocation();

  // --- LOGIKA URL ---
  const queryParams = useMemo(() => parse(location.search), [location.search]);
  const {
    bookingState, notResponded, phrase, startDate,
    endDate, nonDeliveredOnTime, deliveryState,
    deliveryToProviderId, deliveryType, internalUse
  } = queryParams;

  const [searchPhrase, setSearchPhrase] = useState(phrase || '');

  // Centralny punkt aktualizacji URL - eliminuje duplikację kodu
  const updateQueryParams = useCallback((newParams) => {
    const nextParams = { ...queryParams, ...newParams, page: 1 };

    // Czyścimy parametry, które mają być usunięte (undefined)
    const cleanedParams = Object.keys(nextParams).reduce((acc, key) => {
      if (nextParams[key] !== undefined && nextParams[key] !== null) {
        acc[key] = nextParams[key];
      }
      return acc;
    }, {});

    history.push(`${location.pathname}?${stringify(cleanedParams)}`);
  }, [history, location.pathname, queryParams]);

  // --- HANDLERY ---
  const handleMainFilterChange = e => {
    const val = e.target.value;
    const state = val === 'all' ? undefined : val;
    // Logika biznesowa: resetujemy daty tylko jeśli status tego wymaga (np. cancelled/all)
    const shouldResetDates = state === 'cancelled' || val === 'all';

    updateQueryParams({
      bookingState: state,
      startDate: shouldResetDates ? undefined : startDate,
      endDate: shouldResetDates ? undefined : endDate,
    });
  };

  const handleInternalUseChange = e => {
    const val = e.target.value;
    updateQueryParams({ internalUse: val === 'all' ? undefined : val });
  };

  const handleDeliveryStateChange = e => {
    const val = e.target.value;
    updateQueryParams({ deliveryState: val === 'all' ? undefined : val });
  };

  const handleDateChange = (key) => (dateValue) => {
    const formatDate = dateValue ? dateValue.toISOString().split('T')[0] : undefined;
    updateQueryParams({ [key]: formatDate });
  };

  const handleSearchSubmit = e => {
    e.preventDefault();
    updateQueryParams({ phrase: searchPhrase });
  };

  const handleDeliveryLocationChange = ({ value }) => {
    if (value === 'home') {
      updateQueryParams({ deliveryType: 'home', deliveryToProviderId: undefined });
    } else if (value === '') {
      updateQueryParams({ deliveryType: undefined, deliveryToProviderId: undefined });
    } else {
      updateQueryParams({ deliveryToProviderId: value, deliveryType: undefined });
    }
  };

  const clearFilters = () => history.push(location.pathname);

  // --- DANE MEMOIZOWANE ---
  const deliveryOptions = useMemo(() => {
    const selectedProvider = currentUserProviders.find(p => p.id.uuid === providerId);
    const baseOptions = [
      { value: '', label: intl.formatMessage({ id: 'TransactionsFilter.all' }) },
      { value: 'home', label: intl.formatMessage({ id: 'TransactionsFilter.deliveryToHome' }) },
    ];

    const providerOptions = selectedProvider?.deliveryToProviders.map(p => ({
      value: p.id.uuid,
      label: p.attributes.name,
    })) || [];

    return [...baseOptions, ...providerOptions];
  }, [currentUserProviders, providerId, intl]);

  const deliveryInputValue = useMemo(() => {
    if (deliveryType === 'home') return { value: 'home', label: intl.formatMessage({ id: 'TransactionsFilter.deliveryToHome' }) };
    if (deliveryToProviderId) return deliveryOptions.find(o => o.value === deliveryToProviderId) || { value: deliveryToProviderId };
    return { value: '', label: intl.formatMessage({ id: 'TransactionsFilter.all' }) };
  }, [deliveryType, deliveryToProviderId, deliveryOptions, intl]);

  // --- EFEKTY ---
  useEffect(() => {
    if (!phrase) setSearchPhrase('');
  }, [phrase]);

  // --- SUB-KOMPONENTY (Renders) ---
  const renderDateFilter = (type, labelId, value) => (
    <div className={css.filterWrapper}>
      <div className={css.labelWithClearButton}>
        <label><FormattedMessage id={labelId} /></label>
        {!!value && (
          <button className={css.clearButton} onClick={() => handleDateChange(type)(null)}>
            <FormattedMessage id="TransactionsFilter.clear" />
          </button>
        )}
      </div>
      <DateInput
        className={css.test}
        isOutsideRange={() => false}
        onChange={d => handleDateChange(type)(d.date)}
        onFocus={() => { }}
        onBlur={() => { }}
        placeholderText={intl.formatMessage({ id: `${labelId}Placeholder` })}
        value={{ date: value ? new Date(value) : null }}
      />
    </div>
  );

  return (
    <div className={css.filter}>
      {/* Główny status */}
      <Select
        value={bookingState || 'all'}
        onChange={handleMainFilterChange}
        label={intl.formatMessage({ id: "TransactionFilter.bookingStatus" })}
      >
        {SELECT_OPTIONS.map(({ value, label, sales }) =>
          (!isSales || sales) && (
            <option key={value} value={value}>{intl.formatMessage({ id: label })}</option>
          )
        )}
      </Select>

      {false &&
        <Select
          value={internalUse || 'all'}
          onChange={handleInternalUseChange}
          label="Wypożyczenie wewnętrzne"
        >
          {INTERNAL_USE_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </Select>
      }

      {/* Wyszukiwarka tekstowa */}
      {isSales && (
        <form onSubmit={handleSearchSubmit}>
          <Input
            label={intl.formatMessage({ id: "TransactionFilter.search" })}
            value={searchPhrase}
            onChange={e => setSearchPhrase(e.target.value)}
            placeholder={intl.formatMessage({ id: 'TransactionFilter.searchTransaction' })}
          />
        </form>
      )}

      {/* Filtry daty */}
      {STATUSES_FOR_DATE_FILTER.includes(bookingState) && isSales && (
        <div className={css.additionalFilters}>
          {renderDateFilter('startDate', 'TransactionsFilter.dateStart', startDate)}
          {renderDateFilter('endDate', 'TransactionsFilter.dateEnd', endDate)}
        </div>
      )}

      {/* Lokalizacja dostawy (Warehouse only) */}
      {currentUserProviders.find(p => p.id.uuid === providerId)?.attributes.isWarehouse && (
        <div className={css.filterWrapper}>
          <AutocompleteSelect
            inputValue={{ value: deliveryInputValue.value }}
            onChange={handleDeliveryLocationChange}
            label={intl.formatMessage({ id: "TransactionsFilter.location" })}
            options={deliveryOptions}
            placeholder={intl.formatMessage({ id: "TransactionsFilter.location" })}
          />
        </div>
      )}

      {/* Specyficzny status dla PL */}
      {isPoland() && (
        <Select
          value={deliveryState || 'all'}
          onChange={handleDeliveryStateChange}
          label="Status dostawy"
        >
          <option value="all">Wszystkie</option>
          <option value="not-delivered">oczekuje na wysyłkę do sklepu / klienta</option>
          <option value="sent-to-store">wysłano do sklepu / klienta</option>
          <option value="received-by-store">odebrano w sklepie / przez klienta</option>
          <option value="sent-to-stock">wysłano do magazynu</option>
          <option value="received">odebrano w magazynie</option>
        </Select>
      )}

      {/* Toggles & Actions */}
      {isSales && (
        <div className={css.togglerAndClear}>
          <div className={css.toggler}>
            <ToggleSwitch
              name={intl.formatMessage({ id: 'ConsoleTransactionPage.nonDeliveredOnTime' })}
              checked={!!nonDeliveredOnTime}
              onChange={v => updateQueryParams({ nonDeliveredOnTime: v.checked || undefined })}
            />
          </div>
          <InlineTextButton className={css.clearBtn} onClick={clearFilters}>
            <FormattedMessage id="TransactionFilter.clear" />
          </InlineTextButton>
          <ToggleSwitch
            name={intl.formatMessage({ id: 'TransactionFilter.onlyNotResponded' })}
            checked={!!notResponded}
            onChange={v => updateQueryParams({ notResponded: v.checked || undefined })}
          />
        </div>
      )}

      {/* Statystyki */}
      {pagination?.totalItems > 0 && (
        <div className={css.elements}>
          <FormattedMessage id="Console.numberOfItems" />:
          <span className={css.number}>{pagination.totalItems}</span>
        </div>
      )}
    </div>
  );
};

export default injectIntl(TransactionsFilter);